import styled, { css } from 'styled-components'
import { color } from 'ui/themes/utils'

const LightboxGallery = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);

  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 1200px;
  padding: 20px;

  @media (max-width: 576px) {
    padding: 12px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  margin-bottom: 16px;
  z-index: 1;

  @media (max-width: 576px) {
    margin-bottom: 12px;
  }
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  cursor: pointer;

  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    color: ${color('white')};
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`

const Title = styled.h3`
  flex: 1;
  text-align: center;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  color: ${color('white')};
  margin: 0;
  padding: 0 12px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  @media (max-width: 576px) {
    font-size: 16px;
  }
`

const Body = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  width: 100%;
`

const Image = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  padding: 0 12px;
  pointer-events: none;

  @media (max-width: 576px) {
    padding: 0 8px;
  }
`

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;

  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  pointer-events: all;

  svg {
    width: 20px;
    height: 20px;
    color: ${color('black')};
  }

  &:hover:not(:disabled) {
    background-color: ${color('white')};
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 576px) {
    width: 40px;
    height: 40px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 16px;
  min-height: 96px;
  flex-shrink: 0;

  @media (max-width: 576px) {
    margin-top: 12px;
    min-height: 96px;
  }
`

const Gallery = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ size = 80, offset = 0 }) => css`
    ${GalleryImagesContainer} {
      width: ${(size + 8) * 5}px;
      height: ${size + 4}px;

      @media (max-width: 576px) {
        width: ${(size + 8) * 3}px;
      }
    }

    ${GalleryImageContainer} {
      height: ${size}px;
      width: ${size}px;
      min-width: ${size}px;
      min-height: ${size}px;
    }

    ${GalleryImages} {
      left: -${offset * (size + 8)}px;
    }
  `}
`

const GalleryImagesContainer = styled.div`
  position: relative;
  overflow: hidden;
`

const GalleryImages = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  transition: left 0.3s ease-in-out;
`

const GalleryImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
  position: relative;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
  }

  ${({ selected }) =>
    selected &&
    css`
      border-color: ${color('white')} !important;
      background-color: rgba(255, 255, 255, 0.1);
    `}

  > * {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
  }

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
  }
`

LightboxGallery.Wrapper = Wrapper
LightboxGallery.Header = Header
LightboxGallery.CloseButton = CloseButton
LightboxGallery.Title = Title
LightboxGallery.Body = Body
LightboxGallery.Image = Image
LightboxGallery.Controls = Controls
LightboxGallery.ControlButton = ControlButton
LightboxGallery.Footer = Footer
LightboxGallery.Gallery = Gallery
LightboxGallery.GalleryImagesContainer = GalleryImagesContainer
LightboxGallery.GalleryImages = GalleryImages
LightboxGallery.GalleryImageContainer = GalleryImageContainer

export default LightboxGallery
